import type { BirdIconDef } from '../types';
import { BIRD_ACCENT, BIRD_WING } from './shared';

/** Hand-crafted bird-themed icons for navigation and primary domains */
export const CUSTOM_BIRD_ICONS: Record<string, BirdIconDef> = {
  HomeIcon: {
    motion: 'bob',
    markup: `<path d="M4 11.5 12 5l8 6.5V19a1 1 0 0 1-1 1h-5v-5H10v5H5a1 1 0 0 1-1-1z"/><circle cx="12" cy="9.5" r="1.4" fill="currentColor"/><path d="M10.5 10.8c.8.9 2.2.9 3 0" stroke-width="1.2"/>`,
  },
  PresentationChartLineIcon: {
    motion: 'wing',
    markup: `<path d="M4 18V6m0 12h16"/><path d="M7 15l3-4 3 2 4-6"/><path d="M16 5c1 .8 2 1.2 3 1" fill="none"/>${BIRD_ACCENT}`,
  },
  ChartBarIcon: {
    motion: 'pulse',
    markup: `<path d="M5 18V8m4 10V5m4 13V10m4 8V6"/><circle cx="19" cy="6" r="1.2" fill="currentColor"/><path d="M17.5 7.2c1 .8 2 .8 3 0" stroke-width="1.1"/>`,
  },
  BellIcon: {
    motion: 'bob',
    markup: `<path d="M12 4a4 4 0 0 0-4 4v2.5c0 .9-.3 1.8-.9 2.5L6 15.5h12l-1.1-2.5c-.6-.7-.9-1.6-.9-2.5V8"/><path d="M10 17a2 2 0 0 0 4 0"/><circle cx="15" cy="6" r="1" fill="currentColor"/>`,
  },
  ClipboardDocumentListIcon: {
    motion: 'peck',
    markup: `<rect x="6" y="4" width="12" height="16" rx="1.5"/><path d="M9 4.5h6a1 1 0 0 1 1 1V6H8V5.5a1 1 0 0 1 1-1z"/><path d="M9 10h6M9 13h4"/><circle cx="17.5" cy="7" r="1" fill="currentColor"/>`,
  },
  SparklesIcon: {
    motion: 'flutter',
    markup: `<path d="M12 3l1 4 4 1-4 1-1 4-1-4-4-1 4-1z"/><path d="M5 6l.6 1.4L7 8l-1.4.6L5 10l-.6-1.4L3 8l1.4-.6z"/><path d="M18 14l.5 1.2 1.3.3-1.3.3-.5 1.2-.5-1.2-1.3-.3 1.3-.3z"/><ellipse cx="19" cy="6" rx="1.6" ry="1.1" fill="currentColor"/>`,
  },
  GlobeAltIcon: {
    motion: 'wing',
    markup: `<circle cx="12" cy="12" r="8"/><path d="M4 12h16M12 4c2.5 2.8 2.5 14.2 0 16M12 4c-2.5 2.8-2.5 14.2 0 16"/><path d="M17 8c1.2.8 2.2 1.8 2.8 3" fill="none"/>`,
  },
  BuildingOfficeIcon: {
    motion: 'bob',
    markup: `<path d="M6 20V6l6-3 6 3v14"/><path d="M9 10h2v8H9zm4 0h2v8h-2z"/><circle cx="15" cy="7" r="1.1" fill="currentColor"/><path d="M14 8.2c.7.7 1.6.7 2.3 0" stroke-width="1"/>`,
  },
  DocumentDuplicateIcon: {
    motion: 'peck',
    markup: `<rect x="8" y="6" width="11" height="14" rx="1"/><rect x="5" y="4" width="11" height="14" rx="1"/><circle cx="14" cy="7" r="1" fill="currentColor"/>`,
  },
  RectangleGroupIcon: {
    motion: 'bob',
    markup: `<path d="M12 3v3M9.5 3.5h5"/><rect x="5" y="6" width="14" height="14" rx="1.2"/><path d="M9 6v14M15 6v14M5 11.5h14"/><path d="M8 15.5h8"/><circle cx="12" cy="13.4" r="1.35" fill="currentColor"/><path d="M10.6 14.4c.9.8 2 .8 2.8 0" stroke-width="1.1"/>`,
  },
  HeartIcon: {
    motion: 'pulse',
    markup: `<path d="M12 19s-6-3.8-6-8.2C6 7.5 8 6 10 6c1.4 0 2 .9 2 .9S12.6 6 14 6c2 0 4 1.5 4 4.8C18 15.2 12 19 12 19z"/><path d="M9.5 9.5c.8-1 2.2-1 3 0" fill="none"/>`,
  },
  BeakerIcon: {
    motion: 'bob',
    markup: `<path d="M9 4h6l2 7.5a4.5 4.5 0 1 1-10 0z"/><path d="M9 7h6"/><circle cx="16" cy="6" r="1" fill="currentColor"/><path d="M15 7.5c.6.6 1.4.6 2 0" stroke-width="1"/>`,
  },
  ShieldCheckIcon: {
    motion: 'pulse',
    markup: `<path d="M12 4l7 3v5c0 4.2-3 7.5-7 8-4-.5-7-3.8-7-8V7z"/><path d="M9.5 12.5 11 14l3.5-4"/><circle cx="16.5" cy="6.5" r="1" fill="currentColor"/>`,
  },
  BanknotesIcon: {
    motion: 'bob',
    markup: `<rect x="3" y="7" width="18" height="10" rx="1.5"/><circle cx="12" cy="12" r="2.2"/><path d="M7 7v10M17 7v10"/><ellipse cx="18" cy="6" rx="1.3" ry=".9" fill="currentColor"/>`,
  },
  ShoppingCartIcon: {
    motion: 'wing',
    markup: `<path d="M6 6h15l-1.5 8H8L6 6z"/><circle cx="9.5" cy="18" r="1.2"/><circle cx="16.5" cy="18" r="1.2"/><path d="M4 6H2"/><path d="M18 5c.8.6 1.5 1.4 2 2.3" fill="none"/>`,
  },
  ReceiptPercentIcon: {
    motion: 'peck',
    markup: `<path d="M7 4h10v16l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5z"/><path d="M10 9h4M10 12h2"/><circle cx="16" cy="7" r="1" fill="currentColor"/>`,
  },
  CreditCardIcon: {
    motion: 'bob',
    markup: `<rect x="3" y="6" width="18" height="12" rx="2"/><path d="M3 10h18"/><circle cx="17" cy="14" r="1" fill="currentColor"/>`,
  },
  CalculatorIcon: {
    motion: 'peck',
    markup: `<rect x="5" y="3" width="14" height="18" rx="2"/><path d="M8 7h8M8 11h2v2H8zm4 0h2v2h-2zm-4 4h2v2H8zm4 0h2v2h-2z"/><ellipse cx="17.5" cy="6" rx="1.2" ry=".9" fill="currentColor"/>`,
  },
  CubeIcon: {
    motion: 'wing',
    markup: `<path d="M12 4 4 8v8l8 4 8-4V8z"/><path d="M4 8l8 4 8-4M12 12v8"/><circle cx="18" cy="6" r="1" fill="currentColor"/>`,
  },
  TruckIcon: {
    motion: 'flutter',
    markup: `<path d="M3 8h11v8H3z"/><path d="M14 10h4l2 3v3h-6"/><circle cx="7" cy="17" r="1.5"/><circle cx="17" cy="17" r="1.5"/>${BIRD_WING}`,
  },
  BuildingOffice2Icon: {
    motion: 'bob',
    markup: `<path d="M5 20V8l7-4 7 4v12"/><path d="M9 11h2v9H9zm4 0h2v9h-2z"/><circle cx="16" cy="7" r="1" fill="currentColor"/>`,
  },
  UserGroupIcon: {
    motion: 'bob',
    markup: `<circle cx="9" cy="9" r="2.2"/><path d="M5 17c0-2.2 1.8-3.5 4-3.5s4 1.3 4 3.5"/><circle cx="16.5" cy="9" r="1.8"/><path d="M14 16.5c0-1.6 1-2.5 2.5-2.5"/><ellipse cx="19" cy="6" rx="1.2" ry=".85" fill="currentColor"/>`,
  },
  ScaleIcon: {
    motion: 'bob',
    markup: `<path d="M12 4v16"/><path d="M6 8h12"/><path d="M6 8 4 13h4zm8 0 2 5h-4z"/><circle cx="17" cy="6" r="1" fill="currentColor"/>`,
  },
  DocumentTextIcon: {
    motion: 'peck',
    markup: `<path d="M8 4h6l4 4v12H8z"/><path d="M14 4v4h4"/><path d="M10 12h6M10 15h4"/><circle cx="17.5" cy="7" r="1" fill="currentColor"/>`,
  },
  PhotoIcon: {
    motion: 'pulse',
    markup: `<rect x="4" y="6" width="16" height="14" rx="2"/><circle cx="9.5" cy="11" r="1.5"/><path d="M4 17l5-4 3 2 4-5 4 7"/><path d="M16 6c1 .7 2 1.1 3 1.2" fill="none"/>`,
  },
  UserCircleIcon: {
    motion: 'bob',
    markup: `<circle cx="12" cy="12" r="8"/><circle cx="12" cy="10" r="2.2"/><path d="M7.5 16.5c1-1.8 2.7-2.7 4.5-2.7s3.5.9 4.5 2.7"/><ellipse cx="17.5" cy="7" rx="1.1" ry=".8" fill="currentColor"/>`,
  },
  ArrowLeftOnRectangleIcon: {
    motion: 'flutter',
    markup: `<path d="M14 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4"/><path d="M10 12h9"/><path d="M13 9l-3 3 3 3"/><ellipse cx="7" cy="7" rx="1.2" ry=".85" fill="currentColor"/>`,
  },
  CogIcon: {
    motion: 'pulse',
    markup: `<circle cx="12" cy="12" r="2.5"/><path d="M12 4v2m0 12v2M4 12h2m12 0h2m-2.9-5.1-1.4 1.4m-9.4 9.4-1.4 1.4m0-12.2 1.4 1.4m9.4 9.4 1.4 1.4"/><circle cx="18" cy="6" r=".9" fill="currentColor"/>`,
  },
  PlusIcon: {
    motion: 'bob',
    markup: `<path d="M12 5v14M5 12h14"/><circle cx="18" cy="6" r="1.2" fill="currentColor"/><path d="M16.5 7c1 .8 2 .8 3 0" stroke-width="1.1"/>`,
  },
  PencilIcon: {
    motion: 'peck',
    markup: `<path d="M16 4l4 4-9 9H7v-4z"/><path d="M14 6l4 4"/><path d="M5 19h4"/><path d="M17 5c.8.6 1.6 1 2.5 1.1" fill="none"/>`,
  },
  TrashIcon: {
    motion: 'bob',
    markup: `<path d="M6 7h12l-1 12H7z"/><path d="M9 7V5h6v2M10 10v6M14 10v6"/><ellipse cx="17" cy="6" rx="1.1" ry=".8" fill="currentColor"/>`,
  },
  EyeIcon: {
    motion: 'pulse',
    markup: `<path d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z"/><circle cx="12" cy="12" r="2.2"/><circle cx="17.5" cy="7" r="1" fill="currentColor"/>`,
  },
  XMarkIcon: {
    motion: 'flutter',
    markup: `<path d="M7 7l10 10M17 7 7 17"/><path d="M16 5.5c.9.7 1.8 1.1 2.8 1.2" fill="none"/>`,
  },
  MagnifyingGlassIcon: {
    motion: 'bob',
    markup: `<circle cx="11" cy="11" r="5"/><path d="M15.5 15.5 20 20"/><circle cx="18" cy="6" r="1" fill="currentColor"/><path d="M17 7.2c.7.6 1.5.6 2.2 0" stroke-width="1"/>`,
  },
  FunnelIcon: {
    motion: 'bob',
    markup: `<path d="M4 5h16l-6 7v5l-4 2v-7z"/><circle cx="18" cy="6" r="1" fill="currentColor"/>`,
  },
  ListBulletIcon: {
    motion: 'peck',
    markup: `<path d="M9 7h11M9 12h11M9 17h11"/><circle cx="5.5" cy="7" r="1" fill="currentColor"/><circle cx="5.5" cy="12" r="1" fill="currentColor"/><circle cx="5.5" cy="17" r="1" fill="currentColor"/>`,
  },
  PresentationChartBarIcon: {
    motion: 'pulse',
    markup: `<path d="M5 18V8m4 10V5m4 13v-6m4 6V6"/><ellipse cx="18.5" cy="6.5" rx="1.2" ry=".85" fill="currentColor"/>`,
  },
  ChevronDownIcon: {
    motion: 'flutter',
    markup: `<path d="M7 10l5 5 5-5"/><path d="M16 6c.8.5 1.5 1.1 2 1.8" fill="none"/>`,
  },
  ChevronUpIcon: {
    motion: 'flutter',
    markup: `<path d="M7 14l5-5 5 5"/><path d="M16 8c.8-.5 1.5-1.1 2-1.8" fill="none"/>`,
  },
  ChevronLeftIcon: {
    motion: 'flutter',
    markup: `<path d="M14 7l-5 5 5 5"/><path d="M8 6c-.5.8-1.1 1.5-1.8 2" fill="none"/>`,
  },
  ChevronRightIcon: {
    motion: 'flutter',
    markup: `<path d="M10 7l5 5-5 5"/><path d="M16 6c.5.8 1.1 1.5 1.8 2" fill="none"/>`,
  },
  CheckCircleIcon: {
    motion: 'pulse',
    markup: `<circle cx="12" cy="12" r="8"/><path d="M8.5 12.5 11 15l4.5-5"/><circle cx="17" cy="7" r="1" fill="currentColor"/>`,
  },
  ExclamationTriangleIcon: {
    motion: 'bob',
    markup: `<path d="M12 5 20 18H4z"/><path d="M12 10v4"/><circle cx="12" cy="16" r=".8" fill="currentColor"/><ellipse cx="17.5" cy="6.5" rx="1.1" ry=".8" fill="currentColor"/>`,
  },
  ClockIcon: {
    motion: 'pulse',
    markup: `<circle cx="12" cy="12" r="8"/><path d="M12 8v4l3 2"/><circle cx="17" cy="7" r="1" fill="currentColor"/>`,
  },
  CurrencyDollarIcon: {
    motion: 'bob',
    markup: `<path d="M12 4v16"/><path d="M9.5 9.5c0-1.5 1.2-2.5 2.5-2.5s2.5 1 2.5 2.5-2.5 2-2.5 3 2.5 2.5 2.5 2.5"/><ellipse cx="18" cy="6" rx="1.1" ry=".8" fill="currentColor"/>`,
  },
  CameraIcon: {
    motion: 'pulse',
    markup: `<path d="M4 9h4l1.5-2h7L18 9h2v9H4z"/><circle cx="12" cy="13" r="2.8"/><path d="M17 6.5c.9.6 1.8 1 2.8 1.1" fill="none"/>`,
  },
  UserIcon: {
    motion: 'bob',
    markup: `<circle cx="12" cy="9" r="2.5"/><path d="M6.5 18c0-2.5 2.2-4 5.5-4s5.5 1.5 5.5 4"/><ellipse cx="17.5" cy="6.5" rx="1.1" ry=".8" fill="currentColor"/>`,
  },
};
